// src/index.ts
import { emit } from "./core/events";
import { coerceEntries, buildRuntimeManifest } from "./core/manifest";
import { scanAllScripts, observeNewScripts, fetchManifest } from "./core/scan";
import { installCspViolationListener } from "./features/csp";
import { checkHeadersLite } from "./features/header-lite";
import { startNativeGuard, startIntegrityGuard } from "./features/integrity";
import { installRequestGuard } from "./features/request-guard";
import { startWasmPing } from "./features/wasm-ping";
import { startWatchdog } from "./features/watchdog";
import type { JSentinelOptions } from "./types";
import { VERSION } from "./version";
import { loadWasm } from "./wasm/loader";

export async function initJSentinel(options: JSentinelOptions) {
  if (typeof window === "undefined") return; // SSR guard
  // Attach CSP listener as early as possible to avoid missing early violations

  if (!options || !options.license || options.license.trim() === "") {
    emit({
      type: "error",
      data: { stage: "license", message: "LICENSE_REQUIRED" },
    });
    return;
  }

  startIntegrityGuard(
    {
      initJSentinel,
      fetchManifest,
      scanAllScripts,
      observeNewScripts,
      loadWasm,
    },
    { intervalMs: 5000, debug: options.debug },
  );

  const manifestPath = options.manifestPath ?? "/manifest.json";
  const watchClipboard = options.watchClipboard !== false;
  const verbose = !!options.verboseChecks;
  const relaxedHosts = options.relaxedRemoteHosts ?? [];

  try {
    // 1) WASM + License
    const wasm = await loadWasm();
    const payload = JSON.stringify({
      license_token: options.license,
      host: location.hostname,
      version: VERSION,
    });
    const gateRaw = wasm.dispatch(1, payload);
    const out = JSON.parse(gateRaw);
    if (!out?.ok) {
      emit({
        type: "error",
        data: {
          stage: "license",
          message: String(out?.code || "LICENSE_DENIED"),
        },
      });
      return;
    }

    installCspViolationListener();
    installRequestGuard(); // fetch/XHR/beacon (Axios dahil)
    checkHeadersLite(); // minimal header visibility
    addEventListener("pageshow", () => checkHeadersLite());

    // 2) Anti-tamper katmanları
    startWatchdog({ debug: options.debug });
    startWasmPing(
      () => loadWasm(),
      {
        debug: options.debug,
      },
      options.license,
    );
    startNativeGuard({
      intervalMs: 4000,
      debug: options.debug,
    });

    // 3) Manifest tarama & features
    const raw = await fetchManifest(manifestPath);
    const entries = coerceEntries(raw);
    const man = buildRuntimeManifest(entries);

    const results = await scanAllScripts(man, verbose, relaxedHosts);
    emit({
      type: "scan.complete",
      data: {
        total: results.length,
        ok: results.filter(Boolean).length,
        mismatched: results.filter((x) => !x).length,
      },
    });

    observeNewScripts(man, verbose, relaxedHosts);

    if (watchClipboard) {
      const { setupClipboardWatcher } = await import("./features/clipboard");
      setupClipboardWatcher();
    }

    emit({ type: "ready", data: { manifest: raw, version: VERSION } });
  } catch (e: any) {
    emit({
      type: "error",
      data: { stage: "init", message: String(e?.message || e) },
    });
  }
}

export { onJSentinel, onJSentinelAny, offJSentinel } from "./core/events";
export { attachConsoleLogger } from "./features/logger";
